import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AddIcon from '@material-ui/icons/Add';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import InstagramIcon from '@material-ui/icons/Instagram';
import FacebookIcon from '@material-ui/icons/Facebook';
import LanguageIcon from '@material-ui/icons/Language';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { format, parseISO } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { getTokens, brandAlpha } from '../../styles/tokens';
import { i18n } from '../../translate/i18n';

// TODO i18n: chaves `kanban.*` novas com defaultValue (i18next aceita nativamente)
const t = (key, fallback) => i18n.t(key, { defaultValue: fallback });

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

// Cores de identidade dos canais (terceiros), não da marca.
const CHANNEL = {
  whatsapp: { Icon: WhatsAppIcon, color: '#25D366', label: 'WhatsApp' },
  instagram: { Icon: InstagramIcon, color: '#E1306C', label: 'Instagram' },
  facebook: { Icon: FacebookIcon, color: '#1877F2', label: 'Facebook' },
  webchat: { Icon: LanguageIcon, color: '#128C7E', label: 'Webchat' },
};

const relativeTime = iso => {
  if (!iso) return { short: '', full: '' };
  const date = parseISO(iso);
  if (Number.isNaN(date.getTime())) return { short: '', full: '' };
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  const full = format(date, 'dd/MM/yyyy HH:mm');
  if (minutes < 1) return { short: t('kanban.now', 'agora'), full };
  if (minutes < 60) return { short: `${minutes} min`, full };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { short: `${hours} h`, full };
  const days = Math.floor(hours / 24);
  if (days < 7) return { short: `${days} d`, full };
  return { short: format(date, 'dd/MM'), full };
};

const useStyles = makeStyles(theme => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    outer: {
      marginBottom: 8,
      outline: 'none',
      '&:focus-visible $card': {
        boxShadow: tk.shadow.glow,
        borderColor: tk.brand,
      },
      '&:hover $remove': { opacity: 1 },
      '@media (hover: none)': {
        '& $remove': { opacity: 1 },
      },
    },
    card: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      padding: 12,
      backgroundColor: tk.paper,
      border: `1px solid ${tk.line}`,
      borderRadius: tk.radius.md,
      boxShadow: tk.shadow.card,
      cursor: 'grab',
      fontFamily: tk.font,
      transition: 'border-color 150ms ease',
      '&:hover': { borderColor: tk.lineStrong },
      '&:active': { cursor: 'grabbing' },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    // arrastando: só borda da marca e sombra suave (sem escala)
    cardDragging: {
      boxShadow: tk.shadow.soft,
      borderColor: tk.brand,
      cursor: 'grabbing',
    },
    top: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      minWidth: 0,
    },
    avatarWrap: {
      position: 'relative',
      flex: 'none',
    },
    avatar: {
      width: 36,
      height: 36,
      fontSize: 13,
      fontWeight: 600,
    },
    channel: {
      position: 'absolute',
      right: -4,
      bottom: -4,
      width: 18,
      height: 18,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tk.paper,
      border: `1px solid ${tk.line}`,
      boxSizing: 'border-box',
      '& svg': { fontSize: 12 },
    },
    body: {
      flex: 1,
      minWidth: 0,
    },
    name: {
      display: 'block',
      minWidth: 0,
      fontFamily: tk.font,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.35,
      color: tk.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    messageRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 2,
      minWidth: 0,
    },
    message: {
      flex: 1,
      minWidth: 0,
      fontFamily: tk.font,
      fontSize: 13,
      lineHeight: 1.4,
      color: tk.textSecondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    messageUnread: {
      color: tk.text,
      fontWeight: 500,
    },
    unread: {
      flex: 'none',
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: tk.radius.pill,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: tk.font,
      fontSize: 11,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      backgroundColor: tk.brand,
      color: tk.brandOn,
    },
    badges: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 4,
      minWidth: 0,
    },
    pill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      maxWidth: '100%',
      padding: '1px 7px',
      borderRadius: tk.radius.badge,
      fontFamily: tk.font,
      fontSize: 11,
      fontWeight: 500,
      lineHeight: 1.5,
      color: tk.status.neutral.fg,
      backgroundColor: tk.status.neutral.bg,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '& svg': { fontSize: 12, flex: 'none' },
    },
    pillDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      flex: 'none',
    },
    pillText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      minWidth: 0,
    },
    value: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      minHeight: 24,
      padding: '2px 8px',
      borderRadius: tk.radius.badge,
      fontFamily: tk.font,
      fontSize: 12,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      color: tk.brand,
      backgroundColor: tk.tint.soft,
      cursor: 'pointer',
      border: '1px solid transparent',
      transition: 'background-color 150ms ease',
      '&:hover': { backgroundColor: tk.tint.medium },
      '&:focus-visible': { outline: 'none', boxShadow: tk.shadow.glow },
    },
    valueEmpty: {
      color: tk.textMuted,
      backgroundColor: 'transparent',
      border: `1px dashed ${tk.lineStrong}`,
      fontWeight: 500,
      '&:hover': { backgroundColor: tk.muted, color: tk.textSecondary },
      '& svg': { fontSize: 14 },
    },
    remove: {
      padding: 4,
      marginLeft: -2,
      color: tk.textMuted,
      opacity: 0,
      transition: 'opacity 150ms ease, color 150ms ease',
      '&:hover': { color: tk.semantic.error, backgroundColor: 'transparent' },
      '&:focus-visible': { opacity: 1 },
      '& svg': { fontSize: 14 },
    },
    // nº do ticket e data: discretos, no rodapé
    meta: {
      marginLeft: 'auto',
      flex: 'none',
      fontFamily: tk.font,
      fontSize: 11,
      color: tk.textMuted,
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    },
    open: {
      padding: 4,
      marginRight: -4,
      color: tk.textMuted,
      '&:hover': { color: tk.brand },
      '& svg': { fontSize: 16 },
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 0,
    },
    label: {
      fontSize: 12.5,
      fontWeight: 600,
      color: tk.textSecondary,
    },
  };
});

const initials = name => {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join('');
};

const KanbanCard = ({ ticket, index, updateTicket }) => {
  const classes = useStyles();
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');

  const contact = ticket.contact || {};
  const channel = CHANNEL[ticket.channel];
  const unread = Number(ticket.unreadMessages) > 0;
  const time = relativeTime(ticket.updatedAt);

  const handleCardClick = () => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const customFields = contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;

  const handleOpenModal = event => {
    event.stopPropagation();
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const updateContactValue = async (contactId, value) => {
    try {
      await api.put(`/contacts/${contactId}`, {
        extraInfo: [{ name: 'valor', value: value.toString() }],
      });
    } catch (error) {
      toastError(error);
    }
  };

  const removeContactValue = async event => {
    event.stopPropagation();
    try {
      await api.put(`/contacts/${ticket.contact.id}`, {
        extraInfo: [],
      });

      if (valueFieldIndex !== -1) {
        customFields.splice(valueFieldIndex, 1);
      }

      updateTicket({ ...ticket });
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);

    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) {
        ticket.contact.extraInfo = [];
      }
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }

    updateTicket({ ...ticket });

    setOpen(false);
  };

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.outer}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleCardClick}
          onKeyDown={handleKeyDown}
          aria-label={`${contact.name || ''} — ticket ${ticket.id}`}
        >
          <div
            className={[classes.card, snapshot.isDragging && classes.cardDragging]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={classes.top}>
              <div className={classes.avatarWrap}>
                <Avatar src={contact.urlPicture} className={classes.avatar}>
                  {initials(contact.name)}
                </Avatar>
                {channel && (
                  <Tooltip title={channel.label}>
                    <span className={classes.channel} style={{ color: channel.color }}>
                      <channel.Icon />
                    </span>
                  </Tooltip>
                )}
              </div>
              <div className={classes.body}>
                <Tooltip title={contact.name || ''}>
                  <Typography component="span" className={classes.name}>
                    {contact.name || '—'}
                  </Typography>
                </Tooltip>
                <div className={classes.messageRow}>
                  <Tooltip title={ticket.lastMessage || ''}>
                    <Typography
                      component="span"
                      className={[classes.message, unread && classes.messageUnread]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {ticket.lastMessage || t('kanban.noMessage', 'Sem mensagens')}
                    </Typography>
                  </Tooltip>
                  {unread && (
                    <span className={classes.unread} aria-label={`${ticket.unreadMessages} não lidas`}>
                      {ticket.unreadMessages}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {(ticket.queue || ticket.whatsapp || ticket.user) && (
              <div className={classes.badges}>
                {ticket.queue && (
                  <span
                    className={classes.pill}
                    style={{
                      backgroundColor: brandAlpha(ticket.queue.color || '#7c7c7c', 0.12),
                    }}
                  >
                    <span
                      className={classes.pillDot}
                      style={{ backgroundColor: ticket.queue.color || '#7c7c7c' }}
                    />
                    <span className={classes.pillText}>{ticket.queue.name}</span>
                  </span>
                )}
                {ticket.whatsapp && ticket.whatsapp.name && (
                  <span className={classes.pill}>
                    <span className={classes.pillText}>{ticket.whatsapp.name}</span>
                  </span>
                )}
                {ticket.user && (
                  <span className={classes.pill}>
                    <PersonOutlineIcon />
                    <span className={classes.pillText}>{ticket.user.name}</span>
                  </span>
                )}
              </div>
            )}

            <div className={classes.footer}>
              {opportunityValue !== null && Number.isFinite(opportunityValue) ? (
                <>
                  <Tooltip title={t('kanban.editValue', 'Editar valor da oportunidade')}>
                    <button type="button" className={classes.value} onClick={handleOpenModal}>
                      {currency.format(opportunityValue)}
                    </button>
                  </Tooltip>
                  <Tooltip title="Remover">
                    <IconButton
                      className={classes.remove}
                      onClick={removeContactValue}
                      size="small"
                      aria-label="Remover valor"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <button
                  type="button"
                  className={`${classes.value} ${classes.valueEmpty}`}
                  onClick={handleOpenModal}
                >
                  <AddIcon />
                  {t('kanban.addValue', 'Valor')}
                </button>
              )}
              <Tooltip title={time.full}>
                <span className={classes.meta}>
                  #{ticket.id}
                  {time.short ? ` · ${time.short}` : ''}
                </span>
              </Tooltip>
              <Tooltip title={t('kanban.openTicket', 'Abrir ticket')}>
                <IconButton
                  className={classes.open}
                  size="small"
                  aria-label={t('kanban.openTicket', 'Abrir ticket')}
                  onClick={event => {
                    event.stopPropagation();
                    handleCardClick();
                  }}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <Dialog
            open={open}
            onClose={handleCloseModal}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              {valueField ? 'Editar' : 'Atribuir'} Valor da Oportunidade
            </DialogTitle>
            <DialogContent>
              <div className={classes.field}>
                <label className={classes.label} htmlFor={`kanban-valor-${ticket.id}`}>
                  Valor
                </label>
                <TextField
                  id={`kanban-valor-${ticket.id}`}
                  type="number"
                  fullWidth
                  autoFocus
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Cancelar</Button>
              <Button onClick={handleSaveValue} color="primary" variant="contained">
                Salvar
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
